import { computed, type ComputedRef, ref } from 'vue';
import { useRoute } from 'vue-router';
import { Language, TranslationMode } from '@lingvo-news/contracts';
import { parseCommonRouteParamsFromParams } from './useCommonRouteParams';
import { getRouteParamsForStoredPreferences, readStoredPreferences, StoredPreferences } from './usePreferencesStorage';

export interface ResolvedPreferences {
	readonly nativeLanguage: ComputedRef<Language | null>;
	readonly learnedLanguage: ComputedRef<Language | null>;
	readonly mode: ComputedRef<TranslationMode>;
	update: () => void;
}

/**
 * Like `useCurrentPreferences()`, but falls back to saved preferences when route params are missing/invalid.
 * Note: localStorage changes are not reactive; this is intended as a convenient read-time resolver.
 */
export function useResolvedPreferences(): ResolvedPreferences {
	const route = useRoute();

	const parsedRoute = computed(() => {
		return parseCommonRouteParamsFromParams({
			nativeLanguage: route.params.nativeLanguage,
			learnedLanguage: route.params.learnedLanguage,
			mode: route.params.mode,
		});
	});

	let stored = ref(readStoredPreferences());
	let storedRoute = computed(() => {
		return stored.value ? getRouteParamsForStoredPreferences(stored.value):null;
	});

	const nativeLanguage = computed<Language | null>(() => parsedRoute.value.nativeLanguage ?? storedRoute.value?.nativeLanguage ?? null);
	const learnedLanguage = computed<Language | null>(() => parsedRoute.value.learnedLanguage ?? storedRoute.value?.learnedLanguage ?? null);
	const mode = computed<TranslationMode>(() => parsedRoute.value.mode ?? storedRoute.value?.mode ?? TranslationMode.Partial);

	const update = () => {
		stored.value = readStoredPreferences();
	}
	return {
		nativeLanguage,
		learnedLanguage,
		mode,
		update,
	};
}


